import { pipe } from 'fp-ts/function';
import { useMemo } from 'react';
import { reduxForm } from 'redux-form';
import { useRepositoryQuery } from '@mews-commander/data-access';
import {
    FinancialDocumentTemplateSelectField,
    LanguageSelectField,
    SelectField,
    useFormValueSelector,
} from '@mews-commander/legacy-form';
import { CommanderThunkDispatch } from '@mews-commander/types';
import { Loader } from '@mews-commander/ui';
import { LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { DropdownItem, Icon, IconName, Stack } from '@optimus-web/core';
import { closeDialog } from '../../../../../modules/dialog/reducer';
import { PRINT_BILL_MODAL_NAME, PRINT_BILL_PDF_INPUT_VALUE } from '../Action/PrintFinancialDocumentAction';
import { PrintAction } from '../Action/types';
import { submitPrintBillModal } from './actions';
import { FormValues as PrintBillModalDialogFormFormValues } from './types';

const fieldNames = {
    language: 'language',
    printer: 'printer',
    template: 'template',
} as const;

const PrintBillModalDialogForm = () => {
    const action: PrintAction | undefined = useFormValueSelector(PRINT_BILL_MODAL_NAME, 'action');

    const { data, isLoading } = useRepositoryQuery('printers', 'getAll', {});

    const physicalPrinterOptions = useMemo(
        () =>
            data
                ? data.map(({ id, name }) => (
                      <DropdownItem key={id} label={name} value={id} leftDetail={<Icon value={IconName.Print} />} />
                  ))
                : [],
        [data]
    );

    const allPrinterOptions = useMemo(
        () => [
            <DropdownItem
                key={PRINT_BILL_PDF_INPUT_VALUE}
                label={<LocalizedText textKey={TextKey.PrintCustom} />}
                value={PRINT_BILL_PDF_INPUT_VALUE}
                leftDetail={<Icon value={IconName.PrintPdf} />}
            />,
            ...physicalPrinterOptions,
        ],
        [physicalPrinterOptions]
    );

    if (isLoading) {
        return <Loader />;
    }

    return (
        <Stack vertical spacing="200">
            {!!action?.allowedTemplateTypes && (
                <FinancialDocumentTemplateSelectField allowedTemplateTypes={action.allowedTemplateTypes} />
            )}
            {action?.withLanguageSelect && <LanguageSelectField />}
            <SelectField name={fieldNames.printer} label={<LocalizedText textKey={TextKey.PrinterPlural} />}>
                {allPrinterOptions}
            </SelectField>
        </Stack>
    );
};

export default pipe(
    PrintBillModalDialogForm,
    reduxForm({
        form: PRINT_BILL_MODAL_NAME,
        onSubmit: (formValues: PrintBillModalDialogFormFormValues, dispatch: CommanderThunkDispatch) =>
            dispatch(submitPrintBillModal(formValues)),
        onSubmitSuccess: (_result: unknown, dispatch: CommanderThunkDispatch) =>
            dispatch(closeDialog(PRINT_BILL_MODAL_NAME)),
    })
);
